/**
 * Admin Studio v1 — useAdminFabEffect
 * Zero-boilerplate hook for pages to register a global FAB action.
 * Handles mount/unmount lifecycle with ownership guard.
 *
 * Usage:
 *   useAdminFabEffect({
 *     id: 'admin-experiences-create',
 *     ariaLabel: 'Skapa upplevelse',
 *     onClick: () => setOpen(true),
 *   });
 */
import { useEffect } from 'react';
import { useAdminFab, type AdminFabConfig } from '@/context/AdminFabContext';

export function useAdminFabEffect(config: AdminFabConfig | null): void {
  const { setFab, clearFab } = useAdminFab();

  useEffect(() => {
    if (config) {
      setFab(config);
      return () => clearFab(config.id);
    }
    // config is null → no FAB for this page
  }, [config?.id, config?.disabled]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally sparse deps: onClick is a closure that may change but we
  // only need to re-register when id or disabled changes. The latest onClick
  // is captured via the config reference stored in context.
}
